import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Stepper from '@material-ui/core/Stepper'
import Step from '@material-ui/core/Step'
import StepLabel from '@material-ui/core/StepLabel'
import StepContent from '@material-ui/core/StepContent'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import Input from './Input'
import UploadFile from './UploadFile'
import MoreInfo from './MoreInfo'
import TermsOfService from './TermsOfService'
import { red } from '@material-ui/core/colors'
import { decadesValidator, validateDay, validateInputNotBlank, validateMonth, validateUserEmail, validateYear } from './inputValidator'
import { ugcUploadValidator } from '@Pass-It-Down/exhibition-client'


const useStyles = makeStyles((theme) => ({
  root: {},
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginBottom: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
}))

function getSteps() {
  return [
    'Your Name',
    'Your Email',
    'Select File (optional)',
    'More Information',
  ]
}


const NAME_STEP = 0,
  EMAIL_STEP = 1,
  FILE_STEP = 2,
  MORE_INFO_STEP = 3

const decades = [
  {
    label: 'Select',
    value: 'Select',
  },
  {
    label: 'From 1800 to 1810',
    value: '1805',
  },
  {
    label: 'From 1811 to 1820',
    value: '1815',
  },
  {
    label: 'From 1821 to 1830',
    value: '1825',
  },
  {
    label: 'From 1831 to 1840',
    value: '1835',
  },
  {
    label: 'From 1841 to 1850',
    value: '1845',
  },
  {
    label: 'From 1851 to 1860',
    value: '1855',
  },
  {
    label: 'From 1861 to 1870',
    value: '1865',
  },
  {
    label: 'From 1871 to 1880',
    value: '1875',
  },
  {
    label: 'From 1881 to 1890',
    value: '1885',
  },
  {
    label: 'From 1891 to 1900',
    value: '1895',
  },
  {
    label: 'From 1901 to 1910',
    value: '1905',
  },
  {
    label: 'From 1911 to 1920',
    value: '1915',
  },
  {
    label: 'From 1921 to 1930',
    value: '1925',
  },
  {
    label: 'From 1931 to 1940',
    value: '1935',
  },
  {
    label: 'From 1941 to 1950',
    value: '1945',
  },
  {
    label: 'From 1951 to 1960',
    value: '1955',
  },
  {
    label: 'From 1961 to 1970',
    value: '1965',
  },
  {
    label: 'From 1971 to 1980',
    value: '1975',
  },
  {
    label: 'From 1981 to 1990',
    value: '1985',
  },
  {
    label: 'From 1991 to 2000',
    value: '1995',
  },
  {
    label: 'From 2001 to 2010',
    value: '2005',
  },
  {
    label: 'From 2011 to 2020',
    value: '2015',
  },
  {
    label: '2021',
    value: '2021',
  },
  {
    label: '2022',
    value: '2022',
  },
  {
    label: '2023',
    value: '2023',
  },
]

export default function VerticalLinearStepper(props) {
  const { api, app, save, addContent, resetContent, clickedTermsofService } = props

  const classes = useStyles()
  const [activeStep, setActiveStep] = useState(0)
  const steps = getSteps()

  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState('')

  const [name, setName] = useState('')
  const [nameError, setNameError] = useState('')

  const [file, setFile] = useState(null)
  const [fileName, setFileName] = useState('')
  const [fileError, setFileError] = useState('')

  const [moreInfo, setMoreInfo] = useState({
    year: '',
    month: '',
    day: '',
    caption: '',
    desc: '',
    location: {
      label: '',
      coords: { lat: '', lng: '' },
    },
  })

  const [moreInfoError, setMoreInfoError] = useState('')
  const [decade, setDecade] = useState(decades[0].value)

  const [checkedTermsOfService, setCheckedTermsOfService] = useState(false)
  const [termsOfServiceError, setTermsOfServiceError] = useState('')
  const [assetId, setAssetId] = useState()

  const saveAsset = async file => {
    const { fullFileUrl } = await uploadFile(file)
    const asset = api.asset().newAsset({ orgId: app.orgId, type: 'IMAGE', link: fullFileUrl })
    const { asset: savedAsset } = await api.asset().save(asset)
    setAssetId(savedAsset.id)
  }

  const uploadFile = async file => {
    const fileName = api.util().formatFileName(file.name)

    const model = { orgId: app.orgId, fileType: file.type, fileSize: file.size, fileName }

    const { errors: presignedUrlErrors, presignedUrl, fullFileUrl } = await api.ugc().getPresignedUrl(model)
    if (presignedUrlErrors.length > 0) return { errors: presignedUrlErrors }

    const fileUploadModel = { data: file, type: file.type, presignedUrl }
    const { error: uploadError, uploaded } = await api.ugc().upload(fileUploadModel)
    if (!uploaded) return { error: uploadError }

    return { error: undefined, fullFileUrl }
  }

  useEffect(() => {
    if (!addContent) return
    setActiveStep(0)
  }, [addContent])

  useEffect(() => {
    if (!resetContent) return
    handleReset()
  }, [resetContent])

  const handleChange = (e, id) => {
    if(id === 'pickFile') {
      const file = e.target.files[0]
      setFile(file)
      setFileName(file.name)
      const upload = { fileType: file.type, fileName: file.name, fileSize: file.size }
      const validator = ugcUploadValidator(upload)
      if (!validator.isValid) {
        setFileError(validator.messages[0].message)
      } else {
        setFileError('')
        saveAsset(file)
      }
    } else if(id === 'name') {
      const value = e.target.value
      setName(value)
      if(value !== '') {
        setNameError('')
      }
    } else {
      const value = e.target.value
      setEmail(value)
      if(value !== '') setEmailError('')
    }
  }

  function validateInput() {
    if (activeStep === NAME_STEP) {
      const { isValid } = validateInputNotBlank({ name }, 'name')
      if (!isValid) {
        setNameError('Name should not be empty')
        return false
      }
    } else if (activeStep === EMAIL_STEP) {
      const { isValid } = validateInputNotBlank({ email }, 'email')
      if (!isValid) {
        setEmailError('Email should not be empty')
        return false
      }

      const { isValid: isValidEmail } = validateUserEmail({ email }, 'email')
      if (!isValidEmail) {
        setEmailError('Email is not valid')
        return false
      }
    } else if (activeStep === FILE_STEP) {
      return true // allow users to submit content without files
    } else {
      if (!checkedTermsOfService) {
        setTermsOfServiceError('Please accept terms of service')
        return false
      }

      const { year, month, day } = moreInfo

      const v = decadesValidator(decade)
      if (!v.isValid) {
        setMoreInfoError(v.message)
        return false
      }

      const yearValidator = validateYear(year)

      if (!yearValidator.isValid) {
        setMoreInfoError(yearValidator.message)
        return false
      }

      const monthValidator = validateMonth(month)

      if (!monthValidator.isValid) {
        setMoreInfoError(monthValidator.message)
        return false
      }

      const dayValidator = validateDay(day)
      if (!dayValidator.isValid) {
        setMoreInfoError(dayValidator.message)
        return false
      }
    }

    return true
  }

  function handleNext() {
    if (validateInput()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  function handleBack() {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  function handleReset() {
    setActiveStep(0)
    setEmail('')
    setEmailError('')
    setName('')
    setNameError('')
    setFile(null)
    setFileName('')
    setFileError('')
    setMoreInfo({
      year: '',
      month: '',
      day: '',
      caption: '',
      desc: '',
      location: {
        label: '',
        coords: { lat: '', lng: '' },
      },
    })
    setMoreInfoError('')
    setDecade(decades[0].value)
    setCheckedTermsOfService(false)
    setTermsOfServiceError('')
    setAssetId()
  }

  const handleKeyDown = (e) => {
    if (e.keyCode !== 13) return
    if (validateInput()) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }

  const handleMoreInfoChange = (moreInfo) => {
    setMoreInfo(moreInfo)
    if(moreInfoError) setMoreInfoError('')
  }

  const handleChangeDecade = newDecade => setDecade(newDecade)

  const handleSave = () => {
    if (!validateInput()) return

    const copy = { ...moreInfo }

    if (!copy.year) copy.year = decades.find((d) => d.value === decade).value

    const data = {
      ...copy,
      name: name,
      email: email,
      file: file,
      assetId
    }

    save(data)
  }

  const handleChangeCheckedTermsOfService = () => {
    setCheckedTermsOfService(prev => !prev)
    if (termsOfServiceError) {
      setTermsOfServiceError('')
    }
  }

  return (
    <div className={classes.root}>
      <Stepper activeStep={activeStep} orientation='vertical'>
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
            <StepContent>
              <Typography component={'span'} >
                {index === NAME_STEP && (
                  <Input
                    id={'name'}
                    placeholder={'Your name'}
                    value={name}
                    change={handleChange}
                    keyDown={handleKeyDown}
                    error={nameError}
                  />
                )}
                {index === EMAIL_STEP && (
                  <Input
                    id={'email'}
                    value={email}
                    placeholder={'Your email'}
                    change={handleChange}
                    keyDown={handleKeyDown}
                    error={emailError}
                  />
                )}

                {index === FILE_STEP && (
                  <UploadFile
                    fileName={fileName}
                    moreInfo={moreInfo}
                    change={handleMoreInfoChange}
                    receiveFile={handleChange}
                    error={fileError}
                  />
                )}

                {index === MORE_INFO_STEP && (
                  <MoreInfo
                    decade={decade}
                    decades={decades}
                    changeDecade={handleChangeDecade}
                    change={handleMoreInfoChange}
                    moreInfo={moreInfo}
                    error={moreInfoError}
                  />
                )}
              </Typography>

              {/* Show Terms of Service section as the last step */}
              {activeStep === steps.length - 1 && (
                <TermsOfService
                  app={app}
                  click={clickedTermsofService}
                  checked={checkedTermsOfService}
                  change={handleChangeCheckedTermsOfService}
                />
              )}

              {termsOfServiceError && (
                <div style={{ fontSize: 16, color: red[500] }}>
                  {termsOfServiceError}
                </div>
              )}

              <div className={classes.actionsContainer}>
                <div>
                  <Button
                    disabled={activeStep === 0}
                    onClick={handleBack}
                    className={classes.button}
                  >
                    Back
                  </Button>
                  <Button
                    style={{ fontSize: 'large' }}
                    variant='contained'
                    color='primary'
                    onClick={
                      activeStep === steps.length - 1 ? handleSave : handleNext
                    }
                    className={classes.button}
                  >
                    {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                </div>
              </div>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </div>
  )
}
